/** @graphql agg **/
import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useFetchAvailableSourcesQuery$data,
	useFetchAvailableSourcesQuery$variables,
} from './__generated__/useFetchAvailableSourcesQuery.graphql.ts';

export const RemoteObjectAvailableSourcesQuery: GraphQLTaggedNode = graphql`
	query useFetchAvailableSourcesQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_remoteObjectAvailableSources(cloudId: $cloudId, spaceKey: $spaceKey)
	}
`;

export type UseFetchAvailableSourcesResult = {
	availableSources: string[];
	isLoading: boolean;
};

type UseFetchAvailableSourcesProps = {
	spaceKey?: string;
	cloudId: string;
	skip?: boolean;
};

export const useFetchAvailableSources = ({
	spaceKey,
	cloudId,
	skip = false,
}: UseFetchAvailableSourcesProps): UseFetchAvailableSourcesResult => {
	const { data, loading } = useQueryInterop<
		useFetchAvailableSourcesQuery$data,
		useFetchAvailableSourcesQuery$variables
	>(RemoteObjectAvailableSourcesQuery, {
		variables: { cloudId, spaceKey: spaceKey ?? '' },
		skip: skip || !spaceKey,
		interopApolloAggClient: true,
	});

	const availableSources = useMemo<string[]>(() => {
		const raw = data?.confluence_remoteObjectAvailableSources;
		if (!raw) {
			return [];
		}
		return [...new Set(raw.filter((source): source is string => Boolean(source)))];
	}, [data]);

	return { availableSources, isLoading: loading };
};
